// src/store/modules/availability.js
import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const availabilityPagination = createPaginationModule('AVAILABILITY');

export default {
  state: {
    availability: [],
    pagination: defaultPagination(),
    availabilityEntities: [],
    saveAvailabilityResponse: null,
    deleteAvailabilityResponse: null,
    availabilityDetails: null,
  },

  mutations: {
    ...availabilityPagination.mutations,

    setAvailability(state, payload) {
      state.availability = payload;
    },
    setAvailabilityEntities(state, payload) {
      state.availabilityEntities = payload;
    },
    setSaveAvailabilityResponse(state, payload) {
      state.saveAvailabilityResponse = payload;
    },
    setDeleteAvailabilityResponse(state, payload) {
      state.deleteAvailabilityResponse = payload;
    },
    setAvailabilityDetails(state, payload) {
      state.availabilityDetails = payload;
    },
  },

  actions: {
    // ✅ Paginated & searchable fetch for person availability
    async fetchPersonAvailability(
      { commit },
      { personId, locationId, peopleGroupId, search = "", page = 1, pageSize = 10 }
    ) {
      try {
        const response = await api.post(`/personajax`, {
          cmd: "getpersonavailability",
          personid: personId,
          locationid: locationId,
          people_group_id: peopleGroupId,
          search,
          page_size: pageSize,
          page_number: page,
        });

        const data = response.data?.data;
        const pagination = response.data?.pagination;

        if (!data) throw new Error("Invalid API response");

        commit("setAvailability", data);

        if (pagination) {
          commit(availabilityPagination.SET_PAGINATION, {
            currentPage: pagination.current_page,
            totalPages: pagination.total_pages,
            totalRecords: pagination.total_records,
            pageSize: pagination.page_size,
            hasPrevious: pagination.has_previous,
            hasNext: pagination.has_next,
          });
        }

        return data;

      } catch (error) {
        handleApiError(error, "Failed to fetch person availability");
        throw error;
      }
    },

    async fetchAvailabilityEntities({ commit }, { personId, locationId, peopleGroupId }) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "getavailabilityentities",
          locationid: locationId,
          personid: personId,
          people_group_id: peopleGroupId,
        });

        if (response?.data) {
          commit("setAvailabilityEntities", response.data);
          return response.data;
        } else {
          throw new Error("Invalid API response");
        }
      } catch (error) {
        handleApiError(error, "Failed to fetch availability entities");
        throw error;
      }
    },

    // ✅ Add availability
    async addAvailability(
      { commit },
      {
        personId,
        locationId,
        peopleGroupId,
        available,
        dateRange,
        applyToEntityId,
        applyToEntityType,
        recurrencePeriod,
        frequency,
        daysOfWeek,
        recurStartDate,
        recurEndDate,
        recurrenceName,
        recurrenceDescription,
        daysofmonth,
        weeksofyear,
        months,
        weekdaysofmonth,
        weeksofmonth
      }
    ) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "addavailability",
          personid: personId,
          locationid: locationId,
          people_group_id: peopleGroupId,
          available,
          dateRange,
          applytoentityid: applyToEntityId,
          applytoentitytype: applyToEntityType,
          recurrenceperiod: recurrencePeriod,
          frequency,
          daysofweek: daysOfWeek,
          recurstartdate: recurStartDate,
          recurenddate: recurEndDate,
          recurrencename: recurrenceName,
          recurrencedescription: recurrenceDescription,
          daysofmonth,
          weeksofyear,
          months,
          weekdaysofmonth,
          weeksofmonth
        });

        commit("setSaveAvailabilityResponse", response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to add availability");
        throw error;
      }
    },

    // ✅ Edit availability
    async editAvailability(
      { commit },
      {
        availabilityid,
        recurrenceid,
        personId,
        locationId,
        peopleGroupId,
        available,
        dateRange,
        applyToEntityId,
        applyToEntityType,
        recurrencePeriod,
        frequency,
        daysOfWeek,
        recurStartDate,
        recurEndDate,
        recurrenceName,
        recurrenceDescription,
        daysofmonth,
        weeksofyear,
        months,
        weekdaysofmonth,
        weeksofmonth
      }
    ) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "editavailability",
          availabilityid,
          recurrenceid,
          personid: personId,
          locationid: locationId,
          people_group_id: peopleGroupId,
          available,
          dateRange,
          applytoentityid: applyToEntityId,
          applytoentitytype: applyToEntityType,
          recurrenceperiod: recurrencePeriod,
          frequency,
          daysofweek: daysOfWeek,
          recurstartdate: recurStartDate,
          recurenddate: recurEndDate,
          recurrencename: recurrenceName,
          recurrencedescription: recurrenceDescription,
          daysofmonth,
          weeksofyear,
          months,
          weekdaysofmonth,
          weeksofmonth
        });

        commit("setSaveAvailabilityResponse", response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, "Failed to edit availability");
        throw error;
      }
    },

    // ✅ Delete availability
    async deleteAvailability({ commit }, { personId, availabilityId, recurrenceId, locationId, peopleGroupId }) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "deleteavailability",
          personid: personId,
          availabilityid: availabilityId,
          recurrenceid: recurrenceId,
          locationid: locationId,
          people_group_id: peopleGroupId,
        });

        commit('setDeleteAvailabilityResponse', response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, 'Failed to delete availability');
        throw error;
      }
    },

    // ✅ Get availability details
    async fetchAvailabilityDetails({ commit }, { personId, availabilityId, locationId, peopleGroupId }) {
      try {
        const response = await api.post(`/manageuserajax`, {
          cmd: "getavailabilitydetails",
          personid: personId,
          availabilityid: availabilityId,
          locationid: locationId,
          people_group_id: peopleGroupId,
        });

        commit('setAvailabilityDetails', response.data);
        return response.data;
      } catch (error) {
        handleApiError(error, 'Failed to fetch availability details');
        throw error;
      }
    },
  },

  getters: {
    availability: (state) => state.availability,
    availabilityPagination: (state) => state.pagination,
    saveAvailabilityResponse: (state) => state.saveAvailabilityResponse,
    availabilityEntities: (state) => state.availabilityEntities,
    availabilityDetails: (state) => state.availabilityDetails,
  },
};